/**
 * Hungarian Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const hu: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Vezérlőpult',
  'nav.funds': 'Alapok',
  'nav.investments': 'Befektetések',
  'nav.investees': 'Befektetési célpontok',
  'nav.esg': 'ESG Felülvizsgálat',
  'nav.compliance': 'Megfelelőség',
  'nav.reports': 'Jelentések',
  'nav.settings': 'Beállítások',
  'nav.users': 'Felhasználók',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Mutatók',
  'nav.taxonomy': 'EU Taxonómia',
  'nav.filings': 'Bejelentések',
  'nav.calendar': 'Naptár',
  'nav.submit': 'Adatok benyújtása',
  'nav.submissions': 'Benyújtásaim',
  'nav.benchmarks': 'Referenciaértékek',
  'nav.messages': 'Üzenetek',
  'nav.portfolio': 'Portfólió',
  'nav.performance': 'Alap teljesítmény',
  'nav.esgSummary': 'ESG Összefoglaló',
  'nav.pipeline': 'Csővezeték',
  'nav.dueDiligence': 'Átvilágítás',
  'nav.impact': 'Hatásmutatók',
  'nav.sdg': 'FFT Feltérképezés',
  'nav.audit': 'Ellenőrzési nyomvonal',
  'nav.snapshots': 'Adatpillanatképek',
  'nav.knowledgeGraph': 'Tudásgráf',
  'nav.geographic': 'Földrajzi',
  'nav.bi': 'BI Stúdió',
  'nav.ai': 'AI Asszisztens',
  'nav.tasks': 'Feladatok',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Szabályozási monitor',

  // Common Actions
  'action.create': 'Létrehozás',
  'action.edit': 'Szerkesztés',
  'action.delete': 'Törlés',
  'action.save': 'Mentés',
  'action.cancel': 'Mégsem',
  'action.submit': 'Benyújtás',
  'action.approve': 'Jóváhagyás',
  'action.reject': 'Elutasítás',
  'action.download': 'Letöltés',
  'action.export': 'Exportálás',
  'action.import': 'Importálás',
  'action.filter': 'Szűrés',
  'action.search': 'Keresés',
  'action.refresh': 'Frissítés',
  'action.close': 'Bezárás',
  'action.back': 'Vissza',
  'action.next': 'Következő',
  'action.previous': 'Előző',
  'action.viewAll': 'Összes megtekintése',
  'action.viewDetails': 'Részletek megtekintése',
  'action.generate': 'Generálás',
  'action.signOut': 'Kijelentkezés',
  'action.addNew': 'Új hozzáadása',
  'action.invite': 'Meghívás',

  // Statuses
  'status.draft': 'Piszkozat',
  'status.submitted': 'Benyújtva',
  'status.validated': 'Érvényesítve',
  'status.approved': 'Jóváhagyva',
  'status.rejected': 'Elutasítva',
  'status.pending': 'Függőben',
  'status.inProgress': 'Folyamatban',
  'status.overdue': 'Késedelmes',
  'status.compliant': 'Megfelelő',
  'status.filed': 'Bejelentve',
  'status.active': 'Aktív',
  'status.inactive': 'Inaktív',
  'status.closed': 'Lezárt',
  'status.fundraising': 'Tőkegyűjtés',
  'status.investing': 'Befektetés',
  'status.harvesting': 'Betakarítás',
  'status.validating': 'Érvényesítés',
  'status.review': 'Felülvizsgálat',
  'status.monitoring': 'Megfigyelés',
  'status.exiting': 'Kilépés',
  'status.pipeline': 'Pipeline',
  'status.due_diligence': 'Átvilágítás',
  'status.disbursed': 'Kifizetett',
  'status.restructured': 'Átstrukturált',
  'status.matured': 'Lejárt',
  'status.written_off': 'Leírt',
  'status.exited': 'Kilépett',
  'status.incomplete': 'Hiányos',
  'status.open': 'Nyitott',
  'status.in_progress': 'Folyamatban',
  'status.completed': 'Befejezett',
  'status.cancelled': 'Visszavont',
  'status.accepted': 'Elfogadott',
  'status.failed': 'Sikertelen',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Portfólió áttekintés',
  'dashboard.complianceStatus': 'Megfelelőségi állapot',
  'dashboard.collectionProgress': 'Gyűjtés előrehaladása',
  'dashboard.activityFeed': 'Tevékenység',
  'dashboard.quickActions': 'Gyorsműveletek',
  'dashboard.investmentPipeline': 'Befektetési csővezeték',
  'dashboard.totalFunds': 'Összes alap',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Aktív befektetések',
  'dashboard.totalInvestees': 'Befektetési célpontok',
  'dashboard.recentActivity': 'Legutóbbi tevékenység',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Összes kötelezettségvállalás',
  'lp.capitalCalled': 'Lehívott tőke',
  'lp.distributed': 'Kiosztott',
  'lp.netPosition': 'Nettó pozíció',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Alap teljesítmény',
  'lp.esgSummary': 'ESG Összefoglaló',
  'lp.reportLibrary': 'Jelentéskönyvtár',
  'lp.taxonomyAligned': 'Taxonómiához igazított',
  'lp.paiCoverage': 'PAI Lefedettség',
  'lp.portfolioPerformance': 'LP Portfólió teljesítmény',

  // Fund Labels
  'fund.name': 'Alap neve',
  'fund.code': 'Alap kódja',
  'fund.vintage': 'Alapítás éve',
  'fund.size': 'Alap mérete',
  'fund.targetSize': 'Célméret',
  'fund.currency': 'Pénznem',
  'fund.strategy': 'Stratégia',
  'fund.status': 'Állapot',
  'fund.sfdrCategory': 'SFDR Kategória',
  'fund.geographyFocus': 'Földrajzi fókusz',
  'fund.sectorFocus': 'Szektorfókusz',

  // Form Labels
  'form.name': 'Név',
  'form.email': 'E-mail',
  'form.country': 'Ország',
  'form.amount': 'Összeg',
  'form.currency': 'Pénznem',
  'form.date': 'Dátum',
  'form.description': 'Leírás',
  'form.notes': 'Megjegyzések',
  'form.type': 'Típus',
  'form.period': 'Időszak',
  'form.year': 'Év',
  'form.required': 'Kötelező',
  'form.optional': 'Opcionális',

  // Table Headers
  'table.fund': 'Alap',
  'table.commitment': 'Kötelezettségvállalás',
  'table.called': 'Lehívott',
  'table.distributed': 'Kiosztott',
  'table.tvpi': 'TVPI',
  'table.status': 'Állapot',
  'table.regulation': 'Szabályozás',
  'table.filingType': 'Bejelentés típusa',
  'table.reportingPeriod': 'Időszak',
  'table.createdAt': 'Létrehozva',
  'table.actions': 'Műveletek',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'EU Taxonómia',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Adatteljessség',
  'compliance.nextDeadline': 'Következő határidő',

  // Reports
  'report.quarterly': 'Negyedéves jelentés',
  'report.annual': 'Éves jelentés',
  'report.paiStatement': 'PAI Nyilatkozat',
  'report.taxonomyReport': 'Taxonómia jelentés',
  'report.generatedAt': 'Generálva',
  'report.version': 'Verzió',

  // General
  'general.loading': 'Betöltés...',
  'general.noData': 'Nincs elérhető adat',
  'general.error': 'Hiba történt',
  'general.retry': 'Újrapróbálás',
  'general.total': 'Összesen',
  'general.page': 'Oldal',
  'general.of': '/',
  'general.showing': 'Megjelenítve',
  'general.results': 'eredmény',
  'general.language': 'Nyelv',
  'general.english': 'Angol',
  'general.french': 'Francia',

  // Tour
  'tour.takeTour': 'Körúnézés indítása',
  'tour.skip': 'Kihagyás',
  'tour.next': 'Következő',
  'tour.prev': 'Előző',
  'tour.finish': 'Befejezés',


  // Tour Content, Form Labels, Table Headers, Actions, Errors (i18n wiring)
  'tour.sidebarTitle': 'Navigáció',
  'tour.sidebarContent': 'Használja az oldalsávot a portfóliókezelés, ESG adatgyűjtés, megfelelőség, jelentések és elemzőeszközök közötti navigáláshoz.',
  'tour.kpisTitle': 'Portfólió áttekintés',
  'tour.kpisContent': 'Ezek a kártyák egy pillantásra mutatják portfólióját — összes alap, AUM, aktív befektetések és befektetett vállalatok száma.',
  'tour.actionsTitle': 'Gyors műveletek',
  'tour.actionsContent': 'Hozzon létre alapokat, adjon hozzá befektetéseket, hívjon meg vállalatokat és készítsen jelentéseket egyetlen kattintással.',
  'tour.languageTitle': 'Nyelv',
  'tour.languageContent': 'Váltson 24 hivatalos EU-nyelv között. A teljes felület azonnal lefordítódik.',
  'tour.aiTitle': 'AI Asszisztens',
  'tour.aiContent': 'Tegyen fel kérdéseket portfóliójáról bármikor. Az AI asszisztens teljes kontextussal rendelkezik alapjairól és befektetéseiről.',
  'action.createFund': 'Alap létrehozása',
  'action.editFund': 'Alap szerkesztése',
  'action.addInvestment': 'Befektetés hozzáadása',
  'action.addInvestee': 'Vállalat hozzáadása',
  'action.editInvestee': 'Vállalat szerkesztése',
  'action.createTemplate': 'Sablon létrehozása',
  'action.createSubmission': 'Beküldés létrehozása',
  'action.createFiling': 'Beadvány létrehozása',
  'action.generateReport': 'Jelentés generálása',
  'action.inviteUser': 'Felhasználó meghívása',
  'action.sendInvitation': 'Meghívó küldése',
  'action.saveChanges': 'Módosítások mentése',
  'form.fundName': 'Alap neve',
  'form.fundCode': 'Alap kódja',
  'form.sfdrCategory': 'SFDR Kategória',
  'form.baseCurrency': 'Alapvaluta',
  'form.targetSize': 'Célméret',
  'form.vintageYear': 'Alapítás éve',
  'form.geographyFocus': 'Földrajzi fókusz',
  'form.sectorFocus': 'Ágazati fókusz',
  'form.investmentCode': 'Befektetési kód',
  'form.instrumentType': 'Eszköztípus',
  'form.maturityDate': 'Lejárati dátum',
  'form.sector': 'Ágazat',
  'form.countryCode': 'Országkód',
  'form.entityType': 'Szervezettípus',
  'form.region': 'Régió',
  'form.staffCount': 'Létszám',
  'form.borrowers': 'Hitelfelvevők',
  'form.contactName': 'Kapcsolattartó neve',
  'form.contactEmail': 'Kapcsolattartó e-mail',
  'form.templateName': 'Sablon neve',
  'form.templateType': 'Sablon típusa',
  'form.indicators': 'Mutatók',
  'form.reportingPeriod': 'Jelentési időszak',
  'form.reportingYear': 'Jelentési év',
  'form.regulation': 'Szabályozás',
  'form.filingType': 'Beadvány típusa',
  'form.reportType': 'Jelentés típusa',
  'form.emailAddress': 'E-mail cím',
  'form.role': 'Szerepkör',
  'form.selectFund': 'Válasszon alapot...',
  'form.selectInvestee': 'Válasszon vállalatot...',
  'table.code': 'Kód',
  'table.investee': 'Vállalat',
  'table.instrument': 'Eszköz',
  'table.amount': 'Összeg',
  'table.currency': 'Pénznem',
  'table.maturity': 'Lejárat',
  'table.name': 'Név',
  'table.country': 'Ország',
  'table.type': 'Típus',
  'table.period': 'Időszak',
  'table.completion': 'Befejezés',
  'table.submitted': 'Beküldve',
  'table.version': 'Verzió',
  'table.indicators': 'Mutatók',
  'table.dueDate': 'Határidő',
  'table.generated': 'Generálva',
  'table.email': 'E-mail',
  'table.role': 'Szerepkör',
  'error.createFailed': 'Létrehozás sikertelen. Próbálja újra.',
  'error.updateFailed': 'Frissítés sikertelen. Próbálja újra.',
  'error.loadFailed': 'Adatok betöltése sikertelen.',
};
